import { type FullAirHandlerUnit, type HistoryEntry } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import type { ChartDataPoint } from "~/helpers/history.helper";
import { m } from "~/paraglide/messages";

import {
  getKeyLabel,
  getNodeLabel,
  isNestedNode,
  resolveEntityId,
  type HistoryPointSelection,
} from "./data-selector.helper";

/** CSS-safe series id shared by the chart config, Recharts data keys and Y-axis grouping. */
export const seriesKey = (entityId: string, point: string): string => `${entityId}__${point}`;

export const formatAhuUnit = (point: string): string => {
  // A defect is a boolean: the substring rules below would otherwise unit it as its own reading.
  if (point.endsWith("Defect")) return "";

  if (point.includes("Temperature") || point.startsWith("setpointStartFrostFree")) {
    return "°C";
  }

  switch (point) {
    case "supplyAirSetpoint":
    case "supplyAirActualSetpoint":
    case "supplyAirCoolingSetpoint":
    case "supplyAirCoolingActualSetpoint":
    case "returnAirSetpoint":
    case "returnAirActualSetpoint":
    case "returnAirCoolingSetpoint":
    case "returnAirCoolingActualSetpoint":
      return "°C";
    case "outsideAirHumidity":
    case "exhaustAirHumidity":
    case "returnAirHumidity":
    case "supplyAirHumidity":
    case "humidity":
    case "humiditySetpoint":
    case "humidityActualSetpoint":
    case "returnAirHumiditySetpoint":
    case "returnAirHumidityActualSetpoint":
    case "supplyAirHumiditySetpoint":
    case "supplyAirHumidityActualSetpoint":
    case "humidityMinSetpoint":
    case "humidityMaxSetpoint":
    case "summerOccHumiditySetpoint":
    case "summerInoccHumiditySetpoint":
    case "winterOccHumiditySetpoint":
    case "winterInoccHumiditySetpoint":
    case "forcing":
    case "opening":
    case "soilingPercentage":
    case "minimumOutsideAirDampersSetpoint":
    case "minimumOutsideAirDampersActualSetpoint":
    case "platesHeatExchangerEfficiency":
      return "%";
    case "outsideAirCO2":
    case "exhaustAirCO2":
    case "returnAirCO2":
    case "returnAirCO2Setpoint":
    case "returnAirCO2ActualSetpoint":
    case "supplyAirCO2":
    case "supplyAirCO2Setpoint":
    case "supplyAirCO2ActualSetpoint":
    case "co2MinSetpoint":
    case "co2MaxSetpoint":
    case "summerOccCO2Setpoint":
    case "summerInoccCO2Setpoint":
    case "winterOccCO2Setpoint":
    case "winterInoccCO2Setpoint":
      return "ppm";
    case "returnAirFlow":
    case "supplyAirFlow":
    case "summerOccFlowSetpoint":
    case "summerInoccFlowSetpoint":
    case "winterOccFlowSetpoint":
    case "winterInoccFlowSetpoint":
    case "flowMinSetpoint":
    case "flowMaxSetpoint":
      return "m³/h";
    case "pressure":
    case "pressureSetpoint":
    case "pressureActualSetpoint":
    case "summerOccPressureSetpoint":
    case "summerInoccPressureSetpoint":
    case "winterOccPressureSetpoint":
    case "winterInoccPressureSetpoint":
    case "pressureMinSetpoint":
    case "pressureMaxSetpoint":
      return "Pa";
    case "elecConso":
      return "kWh";
    case "operationHours":
    case "operationHoursP1":
    case "operationHoursP2":
    case "temporisationSummerMode":
    case "temporisationMidSeason":
    case "temporisationFrostFreeMode":
      return "h";
    default:
      return "";
  }
};

const findEntityPath = (
  obj: unknown,
  entityId: string,
  targetEntityId: string,
  pathSoFar: string[],
  parentKey?: string,
): string[] | undefined => {
  if (entityId === targetEntityId) return pathSoFar;
  if (obj === null || typeof obj !== "object") return undefined;

  const entries = Array.isArray(obj) ? obj.map((item, index) => [String(index), item] as const) : Object.entries(obj);

  for (const [key, value] of entries) {
    if (!isNestedNode(key, value)) continue;

    if (Array.isArray(value)) {
      const containerLabel = getKeyLabel(key);
      for (const [index, item] of value.entries()) {
        const itemEntityId = resolveEntityId(item, entityId);
        const label = getNodeLabel(String(index), item, key);
        const result = findEntityPath(item, itemEntityId, targetEntityId, [...pathSoFar, containerLabel, label], key);
        if (result) return result;
      }
      continue;
    }

    const childEntityId = resolveEntityId(value, entityId);
    const label = getNodeLabel(key, value, parentKey);
    const result = findEntityPath(value, childEntityId, targetEntityId, [...pathSoFar, label], key);
    if (result) return result;
  }

  return undefined;
};

/** Human label mirroring the legacy path, e.g. "CTA 1 > Ventilateurs > Soufflage > État". */
export const getSelectionPathLabel = (
  airHandlerUnits: FullAirHandlerUnit[],
  entityId: string,
  point: string,
): string => {
  for (const airHandlerUnit of airHandlerUnits) {
    const path = findEntityPath(airHandlerUnit, airHandlerUnit.id, entityId, [airHandlerUnit.name]);
    if (path) return [...path, getKeyLabel(point)].join(" > ");
  }
  return getKeyLabel(point);
};

export const getAhuUnitAxesMapping = (selected: HistoryPointSelection[]): Record<string, string[]> => {
  const mapping: Record<string, string[]> = {};

  for (const { entityId, point } of selected) {
    const unit = formatAhuUnit(point);
    const key = seriesKey(entityId, point);
    (mapping[unit] ??= []).push(key);
  }

  return mapping;
};

export const buildAhuChartMetadata = (
  airHandlerUnits: FullAirHandlerUnit[],
  selected: HistoryPointSelection[],
): { seriesKeys: string[]; labels: Record<string, string> } => {
  const seriesKeys = selected.map(selection => seriesKey(selection.entityId, selection.point));
  const labels = Object.fromEntries(
    selected.map(selection => [
      seriesKey(selection.entityId, selection.point),
      getSelectionPathLabel(airHandlerUnits, selection.entityId, selection.point),
    ]),
  );

  return { seriesKeys, labels };
};

export const transformAhuHistoryData = (
  data: HistoryEntry[] | undefined,
  selected: HistoryPointSelection[],
): ChartDataPoint[] => {
  if (!data?.length || !selected.length) return [];

  const keyBySelection = new Map(
    selected.map(selection => [
      `${selection.entityId}:${selection.point}`,
      seriesKey(selection.entityId, selection.point),
    ]),
  );

  const byTimestamp = new Map<number, ChartDataPoint>();

  for (const item of data) {
    const key = keyBySelection.get(`${item.entityId}:${item.point}`);
    if (!key) continue;

    // Sub-minute jitter (often 1 ms) would otherwise create a distinct x-axis point per sample.
    const timestamp = dayjs(item.timestamp).startOf("minute").valueOf();
    if (!Number.isFinite(timestamp)) continue;

    let row = byTimestamp.get(timestamp);
    if (!row) {
      row = { date: timestamp };
      byTimestamp.set(timestamp, row);
    }

    if (typeof item.value === "number") {
      row[key] = parseFloat(item.value.toFixed(1));
      continue;
    }

    if (typeof item.value === "boolean") {
      row[key] = item.value ? 1 : 0;
    }
  }

  return Array.from(byTimestamp.values()).sort((a, b) => a.date - b.date);
};

export const toAhuExportRows = (
  chartData: ChartDataPoint[],
  selected: HistoryPointSelection[],
  airHandlerUnits: FullAirHandlerUnit[],
): Record<string, string | number>[] =>
  chartData.map(row => {
    const exportRow: Record<string, string | number> = {
      [m.date()]: dayjs(row.date).format("L - LT"),
    };

    for (const selection of selected) {
      const key = seriesKey(selection.entityId, selection.point);
      const value = row[key];
      if (value != null) {
        const label = getSelectionPathLabel(airHandlerUnits, selection.entityId, selection.point);
        const unit = formatAhuUnit(selection.point);
        exportRow[unit ? `${label} (${unit})` : label] = value;
      }
    }

    return exportRow;
  });
